import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isApple, purchaseApple } from './applePurchases';
import { auth, functions } from './firebase';
import { signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

// Imports from your new files
import { initNative, celebrationHaptic } from './native';
import AuthForm from './components/AuthForm';
import IntroAnimation from './components/IntroAnimation';
import TimerScreen from './components/TimerScreen';
import RoutineLibrary from './components/RoutineLibrary';
import RoutineStart from './components/RoutineStart';
import ProUpsell from './components/ProUpsell';
import MainOrbScreen from './components/MainOrbScreen';
import { getRoutineProgress, getRoutineTiming } from './routines';
import { FREE_COLOR_KEYS } from './constants';
import { DEMO_STEPS } from './components/Demo';
import useSwipeGestures from './hooks/useSwipeGestures';
import useSessionTimer from './hooks/useSessionTimer';
import useStats from './hooks/useStats';
import useDemo from './hooks/useDemo';
import useOrbs from './hooks/useOrbs';
import useAuthSync from './hooks/useAuthSync';

import './index.css';

// The pristine settings every new session/user starts from. Kept outside the
// component so login-seeding and logout-reset share one source of truth.
const DEFAULT_SETTINGS = {
  orbColor: 'blue',
  selectedAnimal: 'cat', // kids mode: one animal shown on both orbs
  audioEnabled: true,
  hapticsEnabled: true,
  audioVolume: 50,
  orbSize: 3,
  orbSpacing: 40,
  reappearSpeed: 0.5,
  kidsMode: false,
  sessionMinutes: 0, // 0 = timer off
  swipeGestures: true, // diagonal corner swipes (↘ opens Stats)
  hideProgress: false // hide badges, streaks and statistics everywhere
};

// Pro preview builds are opt-in. In production the override is honoured only
// on a Firebase preview-channel hostname (`project--channel.web.app`), never on
// the live Firebase address, custom domain or a native Capacitor build.
export const isProPreviewEnabled = () => {
  if (process.env.REACT_APP_PREVIEW_PRO !== 'true') return false;
  if (process.env.NODE_ENV === 'development') return true;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host.includes('--') && host.endsWith('.web.app');
};

const App = () => {
  // A separately deployed, short-lived preview build may expose Pro features
  // for review. The normal production build never sets this flag; real access
  // still comes only from the protected entitlement document.
  const previewPro = isProPreviewEnabled();
  const [currentPage, setCurrentPage] = useState('onboarding');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [proPrompt, setProPrompt] = useState(null);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [activeRoutine, setActiveRoutine] = useState(null);
  const [routineMode, setRoutineMode] = useState('tap');
  const [routinePaused, setRoutinePaused] = useState(false);
  const routineCompletionRecordedRef = useRef(false);
  const watchSideRef = useRef(1);
  const closeProPrompt = useCallback(() => setProPrompt(null), []);
  const continueWithFreeTap = useCallback(() => {
    setProPrompt(null);
    setSelectedRoutine(null);
    setCurrentPage('timer');
  }, []);

  // Pro trial/checkout: thin wrappers around the Cloud Functions. Errors are
  // re-thrown so ProUpsell's own try/catch can show the right inline message
  // (e.g. "already used your trial") instead of this failing silently.
  const startProTrial = useCallback(async () => {
    const startTrial = httpsCallable(functions, 'startTrial');
    await startTrial();
    // No page navigation needed — the entitlements listener in useAuthSync
    // picks up the new `pro: true` doc live and the UI updates itself.
  }, []);

  const startProCheckout = useCallback(async (plan) => {
    if (isApple) return purchaseApple(plan);
    const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
    const result = await createCheckoutSession({ plan });
    window.location.href = result.data.url;
  }, []);

  // Initialise native shell (status bar, hide splash) — no-op on web.
  useEffect(() => { initNative(); }, []);

  // Always-current page + demo step, readable from stats callbacks without
  // re-subscribing. demoStepRef is owned here because stats, taps and the demo
  // all need it (the demo keeps it in sync with its state below).
  const currentPageRef = useRef('onboarding');
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  const demoStepRef = useRef(-1);

  // Statistics: lifetime + session aggregates, Firestore persistence, and the
  // Stats screen snapshot. The demo never counts toward stats.
  const {
    statsView, setStatsView,
    bankActive, resumeActiveIfMain, flushStats, openStats, startStatsSession,
    resetSessionStats, resetLifetimeStats,
    recordTap, markOrbAppeared, completeRoutine, recordCheckIn,
    loadStats, beginStatsLoad, wipeStats,
    newAchievement, dismissNewAchievement,
  } = useStats({ currentPageRef, demoStepRef });

  // Auth + settings: Firebase listener, Firestore load/save, account deletion.
  // Logout cleanup is composed below (it needs the timer/orb hooks, which in
  // turn need `settings` from here), so it's reached via a ref.
  const onLogoutRef = useRef(null);
  const {
    user, setUser, loading, settings, setSettings, settingsRef,
    saveSettings, flushSettings, deleteAccount, manageSubscription, resendVerificationEmail, isPro, hasBillingAccount, entitlementReady,
  } = useAuthSync({
    defaultSettings: DEFAULT_SETTINGS,
    setCurrentPage,
    onLoadStats: loadStats,
    onBeginStatsLoad: beginStatsLoad,
    onLogoutRef,
  });
  const hasProAccess = isPro || previewPro;
  useEffect(() => {
    if (newAchievement && !settings.hideProgress && settings.hapticsEnabled !== false) celebrationHaptic();
  }, [newAchievement, settings.hideProgress, settings.hapticsEnabled]);
  const finishIntro = useCallback(() => setCurrentPage('main'), []);


  // The website's Pro "Get TapTap Pro" button links here with ?upgrade=1 so
  // the web app jumps straight to the purchase screen instead of stranding
  // people on Home. Native iOS can't link to this itself (App Store
  // anti-steering rule, see ProUpsell) - only the marketing site uses it.
  //
  // Captured once into a ref (and the URL cleaned up immediately) rather than
  // read live: the login/signup this usually triggers is async and its own
  // auth listener unconditionally jumps to the intro, so a live read would
  // lose the query param before the redirect below ever got to act on it.
  const upgradeIntentRef = useRef(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('upgrade') === '1'
  );
  useEffect(() => {
    if (upgradeIntentRef.current) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // The timer's completion feedback should honour locked routine choices too.
  // In particular, Before Sleep finishes without a chime or celebratory haptic.
  const sessionFeedbackSettingsRef = useRef(settings);
  sessionFeedbackSettingsRef.current = activeRoutine?.settings
    ? {
        ...settings,
        ...activeRoutine.settings,
        completionHaptics: activeRoutine.completionHaptics ?? settings.completionHaptics,
      }
    : settings;

  // Shown during the first-launch pause on the orbs, suggesting landscape.
  const [showRotateHint, setShowRotateHint] = useState(false);

  // Bumped to ask the SettingsPanel to open/close itself or scroll to the Kids
  // toggle (used by the swipe-up gesture and the automated Kids Mode demo).
  const [openSettingsSignal, setOpenSettingsSignal] = useState(0);
  const [closeSettingsSignal, setCloseSettingsSignal] = useState(0);

  // First-ever-launch interactive demo (non-destructive: snapshots + restores
  // the user's real settings around the tour).
  const onDemoFinish = useCallback((outcome = 'home') => {
    setCloseSettingsSignal((n) => n + 1); // ensure the panel closes if we skip mid-automation
    // The final Free Tap action goes straight to its timer. Skipping the tour
    // keeps the existing, low-pressure landing on Home.
    setCurrentPage(outcome === 'free-tap' ? 'timer' : 'routines');
  }, []);
  const { demoStep, startDemo, advanceDemo, finishDemo, handleDemoTap } = useDemo({
    demoStepRef,
    settingsRef,
    setSettings,
    defaultSettings: DEFAULT_SETTINGS,
    onFinish: onDemoFinish,
  });
  const continueOnboardingWithFreeTap = useCallback(() => {
    setProPrompt(null);
    finishDemo('free-tap');
  }, [finishDemo]);
  const showOnboardingProOverview = useCallback(() => setProPrompt('overview'), []);

  // Act on the ?upgrade=1 intent (see upgradeIntentRef above) once we can:
  // signed in, and entitlement resolved so we know they're not Pro already.
  // Overrides wherever navigation has otherwise landed - including bailing a
  // first-ever-launch demo mid-tour - since the point of this link is to
  // reach checkout as directly as possible, not tour the app first.
  useEffect(() => {
    if (!upgradeIntentRef.current || !user || !entitlementReady || hasProAccess) return;
    upgradeIntentRef.current = false;
    try { localStorage.setItem('taptap_demoSeen', '1'); } catch { /* ignore */ }
    if (demoStep >= 0) finishDemo();
    setCurrentPage('routines');
    setProPrompt('routines');
  }, [user, entitlementReady, hasProAccess, demoStep, finishDemo]);

  // Session lifecycle: entering the orb screen starts a stats session; leaving
  // it banks the active time. On a first-ever launch we run the demo instead of
  // starting a session, so the tour is never counted as real use.
  useEffect(() => {
    if (currentPage === 'main') {
      let willDemo = false;
      try { willDemo = !localStorage.getItem('taptap_demoSeen'); } catch { /* ignore */ }
      if (willDemo) {
        // Let the spheres sit in their start state for a beat (with a hint to
        // rotate to landscape), then the demo text box appears.
        setShowRotateHint(true);
        const id = setTimeout(() => { setShowRotateHint(false); startDemo(); }, 3500);
        return () => clearTimeout(id);
      }
      startStatsSession();
    } else {
      bankActive();
    }
  }, [currentPage, startStatsSession, bankActive, startDemo]);

  // Session timer: countdown, end-of-session chime + haptics, resume prompt.
  // Paused (and resumed) automatically while the settings panel is open.
  const {
    secondsLeft, awaitingResume, startTimer, resumeFromSession, resetTimer,
  } = useSessionTimer({
    settingsRef: sessionFeedbackSettingsRef,
    paused: settingsOpen || routinePaused,
    onSessionEnd: bankActive,
    onResume: resumeActiveIfMain,
  });

  // The two orbs: visibility, colour/animal cycling, sounds and the tap handler.
  const onOrbTapped = useCallback((orbNumber) => {
    // Stats: count the tap and time it from when this orb last appeared
    // (skipped internally during the demo so it never lands in the stats).
    recordTap(orbNumber);
    // Advance the interactive demo's tap-driven steps.
    handleDemoTap(orbNumber);
  }, [recordTap, handleDemoTap]);
  // Keep this object stable while the countdown re-renders. If it were rebuilt
  // every second, useOrbs would rebuild its handler and Watch mode would restart
  // at the left orb instead of continuing left → right.
  const effectiveSettings = useMemo(() => activeRoutine?.settings
    ? { ...settings, ...activeRoutine.settings }
    : settings, [settings, activeRoutine]);
  // Hold the final visual state beneath the completion dialog instead of
  // snapping a finished sunrise/dusk journey back to its opening colour.
  const routineSecondsForPresentation = awaitingResume && activeRoutine ? 0 : secondsLeft;
  const routineProgress = getRoutineProgress(activeRoutine, routineSecondsForPresentation);
  const routineTiming = getRoutineTiming(activeRoutine, routineSecondsForPresentation);
  // Read by callbacks without making them restart every second as Wake Up's
  // pace gradually changes through the session.
  const routineReappearSpeedRef = useRef(null);
  routineReappearSpeedRef.current = activeRoutine ? routineTiming.reappearSpeed : null;
  const {
    orb1Visible, orb2Visible, orb1Color, orb2Color, orb1Animal, orb2Animal,
    handleOrbTap, resetOrbs,
  } = useOrbs({
    settings: effectiveSettings,
    onTap: onOrbTapped,
    onOrbReappeared: markOrbAppeared,
    reappearSpeedRef: routineReappearSpeedRef,
  });

  // “Watch the rhythm” alternates the same orb behaviour without recording
  // synthetic taps, reaction times or haptics. Time and completion still count.
  useEffect(() => {
    if (currentPage !== 'main' || !activeRoutine || routineMode !== 'watch' || settingsOpen || routinePaused || awaitingResume) return;
    let cancelled = false;
    let timeoutId;
    const scheduleNext = (delayMs) => {
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        handleOrbTap(watchSideRef.current, { automatic: true });
        watchSideRef.current = watchSideRef.current === 1 ? 2 : 1;
        const liveSpeed = Number(routineReappearSpeedRef.current ?? effectiveSettings.reappearSpeed ?? 1);
        scheduleNext(Math.max(700, liveSpeed * 1000 + 250));
      }, delayMs);
    };
    scheduleNext(700);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [currentPage, activeRoutine, routineMode, settingsOpen, routinePaused, awaitingResume, effectiveSettings.reappearSpeed, handleOrbTap]);

  useEffect(() => {
    if (!awaitingResume || !activeRoutine || routineCompletionRecordedRef.current) return;
    routineCompletionRecordedRef.current = true;
    completeRoutine(activeRoutine.id);
  }, [awaitingResume, activeRoutine, completeRoutine]);

  // A live cancellation/revocation must take effect without a relaunch. Reset
  // any paid-only presentation and exit a routine cleanly once the entitlement
  // listener has resolved. This is deliberately fail-closed.
  // Exception: the onboarding demo's Kids Mode preview turns kidsMode on
  // locally (never saved) for a free user to show what Pro looks like — this
  // effect would otherwise instantly stomp that back off.
  const isKidsPreview = DEMO_STEPS[demoStep]?.key === 'kids';
  useEffect(() => {
    if (!entitlementReady || hasProAccess || isKidsPreview) return;

    const settingsPatch = {};
    if (settings.kidsMode) settingsPatch.kidsMode = false;
    if (!FREE_COLOR_KEYS.includes(settings.orbColor)) settingsPatch.orbColor = 'blue';
    if (Object.keys(settingsPatch).length) saveSettings(settingsPatch);

    if (activeRoutine || selectedRoutine) {
      resetTimer();
      resetOrbs();
      setActiveRoutine(null);
      setSelectedRoutine(null);
      setRoutinePaused(false);
      setCurrentPage('routines');
      setProPrompt('ended');
    }
  }, [
    entitlementReady, hasProAccess, isKidsPreview, settings.kidsMode, settings.orbColor,
    saveSettings, activeRoutine, selectedRoutine, resetTimer, resetOrbs,
  ]);

  // Logout cleanup for the auth hook: clear the session timer, restore the
  // orbs, close the panel and wipe in-memory stats so nothing leaks into the
  // next login. Kept in a ref (updated every render) because these hooks are
  // composed after useAuthSync but its listener mounts once.
  useEffect(() => {
    onLogoutRef.current = () => {
      resetTimer();
      resetOrbs();
      setSettingsOpen(false);
      setProPrompt(null);
      setRoutinePaused(false);
      setActiveRoutine(null);
      setSelectedRoutine(null);
      wipeStats();
    };
  }, [resetTimer, resetOrbs, wipeStats]);

  // Corner swipes: ↘ (down-right) opens Stats, ↑ (up) opens Settings.
  // Disabled while a modal/panel is open or the swipe setting is off; during the
  // demo only the final 'stats' step accepts a swipe (to let the user try it).
  const demoBlocksSwipe = demoStep >= 0 && DEMO_STEPS[demoStep]?.key !== 'stats';
  const swipe = useSwipeGestures({
    enabled: !(activeRoutine || settingsOpen || statsView || demoBlocksSwipe || awaitingResume || !(settings.swipeGestures ?? true)),
    onSwipeDownRight: openStats,
    onSwipeUp: () => setOpenSettingsSignal((n) => n + 1),
  });

  // flushStats already fails silently on a bad connection (see useStats), so
  // don't await it here - a slow/offline network shouldn't be able to block
  // signing out.
  const handleSignOut = () => { bankActive(); flushStats(); flushSettings?.(); signOut(auth); };

  // Loading Screen
  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-slate-900 text-white font-bold">
      Loading TapTap...
    </div>
  );

  // Page Routing Logic
  if (!user) return <AuthForm onLogin={setUser} />;
  
  if (currentPage === 'intro') {
    return (
      <IntroAnimation onDone={finishIntro} />
    );
  }

  if (currentPage === 'routines') {
    return (
      <>
        <RoutineLibrary
          isPro={hasProAccess}
          accessPending={!previewPro && !entitlementReady}
          onFreeTap={() => setCurrentPage('timer')}
          onChooseRoutine={(routine) => {
            if (!hasProAccess) { setProPrompt('routines'); return; }
            setSelectedRoutine(routine);
            setCurrentPage('routineStart');
          }}
          onProRequired={(feature) => setProPrompt(feature || 'routines')}
          onSignOut={handleSignOut}
        />
        {proPrompt && (
          <ProUpsell
            feature={proPrompt}
            onClose={closeProPrompt}
            onContinueFree={continueWithFreeTap}
            onStartTrial={startProTrial}
            onUpgrade={startProCheckout}
            onResendVerification={resendVerificationEmail}
            hasProAccess={hasProAccess}
          />
        )}
      </>
    );
  }

  if (currentPage === 'routineStart' && selectedRoutine) {
    if (!hasProAccess) {
      return (
        <>
          <RoutineLibrary
            isPro={false}
            accessPending={!entitlementReady}
            onFreeTap={() => setCurrentPage('timer')}
            onChooseRoutine={() => setProPrompt('routines')}
            onProRequired={() => setProPrompt('routines')}
          />
          {proPrompt && (
            <ProUpsell
              feature={proPrompt}
              onClose={closeProPrompt}
              onContinueFree={continueWithFreeTap}
              onStartTrial={startProTrial}
              onUpgrade={startProCheckout}
              onResendVerification={resendVerificationEmail}
              hasProAccess={hasProAccess}
            />
          )}
        </>
      );
    }
    return (
      <RoutineStart
        routine={selectedRoutine}
        onBack={() => setCurrentPage('routines')}
        onStart={(mode) => {
          if (!hasProAccess) {
            setSelectedRoutine(null);
            setCurrentPage('routines');
            setProPrompt('routines');
            return;
          }
          routineCompletionRecordedRef.current = false;
          watchSideRef.current = 1;
          setRoutinePaused(false);
          setRoutineMode(mode);
          setActiveRoutine(selectedRoutine);
          resetOrbs();
          startTimer(selectedRoutine.durationMinutes);
          setCurrentPage('main');
        }}
      />
    );
  }

  if (currentPage === 'timer') {
    return (
      <TimerScreen
        initialMinutes={settings.sessionMinutes ?? 0}
        onBegin={(minutes) => {
          saveSettings({ sessionMinutes: minutes });
          startTimer(minutes);
          setCurrentPage('main');
        }}
      />
    );
  }

  // Only the orb screen lives below. Guard against the brief window right after
  // login where `user` is set but the auth listener hasn't moved us to the
  // disclaimer yet — without this we'd flash the orbs before "Welcome".
  if (currentPage !== 'main') return <div className="h-screen w-full bg-black" />;

  return (
    <MainOrbScreen
      settings={settings} setSettings={setSettings} saveSettings={saveSettings}
      hasProAccess={hasProAccess} hasBillingAccount={hasBillingAccount} isKidsPreview={isKidsPreview}
      settingsOpen={settingsOpen} setSettingsOpen={setSettingsOpen}
      openSettingsSignal={openSettingsSignal} closeSettingsSignal={closeSettingsSignal}
      advanceDemo={advanceDemo} handleSignOut={handleSignOut}
      deleteAccount={deleteAccount} manageSubscription={manageSubscription} setProPrompt={setProPrompt}
      startTimer={startTimer} openStats={openStats} statsView={statsView} setStatsView={setStatsView}
      resetSessionStats={resetSessionStats} resetLifetimeStats={resetLifetimeStats}
      demoStep={demoStep} demoStepRef={demoStepRef} showRotateHint={showRotateHint}
      secondsLeft={secondsLeft} resumeFromSession={resumeFromSession}
      activeRoutine={activeRoutine} awaitingResume={awaitingResume} routineMode={routineMode}
      routinePaused={routinePaused} setRoutinePaused={setRoutinePaused}
      routineTiming={routineTiming} routineProgress={routineProgress}
      setActiveRoutine={setActiveRoutine} setSelectedRoutine={setSelectedRoutine} setCurrentPage={setCurrentPage}
      resetTimer={resetTimer} resetOrbs={resetOrbs} recordCheckIn={recordCheckIn}
      continueOnboardingWithFreeTap={continueOnboardingWithFreeTap} showOnboardingProOverview={showOnboardingProOverview}
      finishDemo={finishDemo}
      effectiveSettings={effectiveSettings} orb1Animal={orb1Animal} orb2Animal={orb2Animal}
      orb1Visible={orb1Visible} orb2Visible={orb2Visible} orb1Color={orb1Color} orb2Color={orb2Color}
      handleOrbTap={handleOrbTap} proPrompt={proPrompt} closeProPrompt={closeProPrompt}
      startProTrial={startProTrial} startProCheckout={startProCheckout}
      resendVerificationEmail={resendVerificationEmail}
      newAchievement={newAchievement} dismissNewAchievement={dismissNewAchievement} swipe={swipe}
    />
  );
};

export default App;
